import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMemo, useState } from "react";
import { useFinance } from "./FinanceContext";
import { activeVer, now, sumTo } from "./data/model";
import { SEED } from "./data/seedData";
import { AnnualBar } from "./components/AnnualBar";
import { MonthPickerRow } from "./components/MonthPicker";
import { FreezeStrip } from "./components/FreezeStrip";
import { RecentChanges } from "./components/RecentChanges";
import { KpiGrid, type KpiItem } from "./components/KpiGrid";
import {
  BudgetVsActualChart,
  DepartmentPerformanceChart,
  RevenueVsBudgetChart,
  TopVarianceChart,
} from "./components/charts/DashboardCharts";

export function DashboardPage() {
  const {
    model,
    fmt,
    scf,
    scenario,
    selMonth,
    staged,
    lastChange,
    locks,
    versions,
    role,
    commentary,
    setCommentary,
    userName,
  } = useFinance();
  const [note, setNote] = useState("");

  const a = useMemo(() => {
    let direct = 0;
    let opex = 0;
    model.items.forEach((it) => {
      const av = sumTo(it.actual, selMonth);
      if (it.type === "Direct") direct += av;
      else opex += av;
    });
    const approved = model.items.reduce((s, i) => s + i.approved, 0);
    const pm = Array(12).fill(0);
    const am = Array(12).fill(0);
    model.items.forEach((it) => {
      it.planned.forEach((v, m) => (pm[m] += v * scf));
      it.actual.forEach((v, m) => (am[m] += v));
    });
    const rvA = Array(12).fill(0);
    model.revenue.forEach((r) => r.actual.forEach((v, m) => (rvA[m] += v)));
    const ytdB = sumTo(pm, selMonth);
    const ytdA = sumTo(am, selMonth);
    const totR = sumTo(rvA, selMonth);
    return {
      approved,
      ytdB,
      ytdA,
      totR,
      direct,
      opex,
      fc: Math.round((ytdA / (selMonth + 1)) * 12),
    };
  }, [model, scf, selMonth]);

  const util = a.ytdB ? (a.ytdA / a.ytdB) * 100 : 0;
  const net = a.totR - a.ytdA;
  const ebitda = a.totR - a.direct - a.opex;
  const burn = Math.round(a.ytdA / (selMonth + 1));

  const notif: [string, string | number][] = [
    [
      "Pending Approval",
      (staged && staged.status !== "Published" ? 1 : 0) +
        versions.filter((v) => v.status === "Revision").length,
    ],
    ["Frozen Months", locks.filter(Boolean).length],
    ["Latest Version", activeVer(versions)],
    [
      "Budget Revisions",
      versions.filter((v) => v.status === "Revision" || /\.[1-9]/.test(v.ver)).length,
    ],
    ["Revenue Changes", lastChange.pages.revenue ?? 0],
    ["Pending Uploads", staged && staged.status === "Draft" ? 1 : 0],
  ];

  const kpis: KpiItem[] = [
    { label: "Annual Approved", value: fmt(a.approved) },
    { label: "YTD Actual", value: fmt(a.ytdA) },
    { label: "Revenue (YTD)", value: fmt(a.totR) },
    { label: "Remaining", value: fmt(a.approved - a.ytdA) },
    { label: "Forecast", value: fmt(a.fc) },
    { label: "Variance", value: fmt(a.ytdB - a.ytdA), tone: a.ytdB - a.ytdA >= 0 ? "pos" : "neg" },
    {
      label: "Utilization",
      value: util.toFixed(1) + "%",
      tone: util > 100 ? "r" : util > 90 ? "a" : "g",
      showBadge: true,
    },
    { label: "Net Profit", value: fmt(net), tone: net >= 0 ? "pos" : "neg" },
    { label: "EBITDA (approx)", value: fmt(ebitda), tone: ebitda >= 0 ? "pos" : "neg" },
    { label: "Monthly Burn Rate", value: fmt(burn) },
    { label: "Cash Position", value: fmt(a.totR - a.ytdA) },
  ];

  const canNote = role === "CEO" || role === "Director" || role === "Finance Head";
  function addNote() {
    if (!note.trim()) return;
    setCommentary((c) => [
      { id: "c" + Date.now(), by: userName, role, date: now(), text: note.trim() },
      ...c,
    ]);
    setNote("");
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Executive Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {SEED.fiscalYear} · through {SEED.months[selMonth]} · scenario: <strong>{scenario}</strong>{" "}
        · signed in as <strong>{userName}</strong> ({role})
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
          gap: 1.5,
          mb: 2,
        }}
      >
        {notif.map(([label, value]) => (
          <Paper key={label} variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {value}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {label}
            </Typography>
          </Paper>
        ))}
      </Box>

      <AnnualBar />
      <MonthPickerRow />
      <FreezeStrip />
      <KpiGrid items={kpis} />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Annual Budget vs Actual (YTD)
        </Typography>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, util)}
          color={util > 100 ? "error" : util > 90 ? "warning" : "success"}
          sx={{ height: 24, borderRadius: 1 }}
        />
        <Typography variant="caption" color="text.secondary">
          {util.toFixed(1)}% consumed · {fmt(a.ytdA)} of {fmt(a.approved)}
        </Typography>
      </Paper>

      <Box
        sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 2 }}
      >
        <BudgetVsActualChart />
        <RevenueVsBudgetChart />
      </Box>
      <Box
        sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 2 }}
      >
        <TopVarianceChart />
        <DepartmentPerformanceChart />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <RecentChanges />
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Executive Commentary
          </Typography>
          {canNote ? (
            <Stack spacing={1} sx={{ mb: 1.5 }}>
              <TextField
                multiline
                minRows={2}
                size="small"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a board-reporting note on a revision or variance…"
              />
              <Box>
                <Button size="small" variant="contained" onClick={addNote}>
                  Post note
                </Button>
              </Box>
            </Stack>
          ) : null}
          <Stack spacing={1}>
            {commentary.map((c) => (
              <Paper key={c.id} variant="outlined" sx={{ p: 1.25, bgcolor: "action.hover" }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  {c.by} · {c.role} · {c.date}
                </Typography>
                <Typography variant="body2">{c.text}</Typography>
              </Paper>
            ))}
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
